// The Dashboard as widgets: the Classic layout (the original Dashboard), plus the member's own
// named dashboards (admin-only preview for now, picked from one dropdown). Customize is an edit
// mode: a dotted grid, S/M/L sizes, drag to move, remove with Undo, and a library drawer that
// previews each widget with the member's own numbers. Changes save on their own to the account
// (user.dashboards), so they're the same on every device.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { AccountOverview, AccountStatusCard } from './AccountStatus';
import Calendar from './Calendar';
import { DailyBars, EquityCurve, InsightCard, Nudges, PerformancePanel, Ring, Sparkline, Tile, performanceTiles } from './Dashboard';
import InfoTip from './InfoTip';
import { TodayEvents } from './MarketCalendar';
import PeriodPanels from './PeriodPanels';
import { accountStatus } from './balance';
import { currentStreaks, dayNets, dayWinRate, drawdownSeries, expectancy, expectancyParts, longestStreaks, maxDrawdown } from './dashboardMetrics';
import { currentTradingDay, formatMoney, netPnl, tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { featureOpen } from './AppNav';
import DashboardOverview from './DashboardOverview';
import DashboardThree, { LeakCard, TradeTable } from './DashboardThree';
import { STYLES, StyledWidget, styleOf } from './StyledWidgets';
import { MarkFocus, OpenPositionsWidget, PnlBySymbol, PnlByWeekday, RulesToday } from './MoreWidgets';
import { isPropAccount } from './DashboardOverview';
import { ScoreBar, ScoreHeading, TraderScoreSection } from './ScoreViews';
import { LatestDayInsights } from './DayInsightsCard';
import { traderScore } from './traderScore';
import DashGrid from './DashGrid';
import { WIDTH, compact, freeSlots, guessH, readingOrder, sizeOfW, toGrid } from './gridLayout';

const isGap = (w) => Boolean(w && typeof w.id === 'string' && /^gap/.test(w.id));
const gridInt = (n, lo, hi) => Number.isInteger(n) && n >= lo && n <= hi;

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const SPAN = { small: 3, medium: 6, large: 12 };
const SIZE_TEXT = { small: 'S', medium: 'M', large: 'L' };
const SIZE_NAME = { small: 'Small', medium: 'Medium', large: 'Large' };
const SIZE_HINT = { small: 'Small: a quarter of the width', medium: 'Medium: half the width', large: 'Large: the full width' };

// Every widget: what it is, where it sits in the library and the sizes it fits.
export const WIDGETS = [
  { id: 'propProgress', group: 'Account rules', title: 'Account progress', desc: 'Where each account sits between its max loss and its profit target.', sizes: ['medium', 'large'] },
  { id: 'dailyLoss', group: 'Account rules', title: 'Daily loss left', desc: 'How much room is left today before your daily loss limit.', sizes: ['small', 'medium'] },
  { id: 'consistency', group: 'Account rules', title: 'Consistency rule', desc: 'Your best day as a share of your profit, against your firm’s limit.', sizes: ['small', 'medium'] },
  { id: 'payouts', group: 'Account rules', title: 'Payout tracker', desc: 'Winning days toward your next payout, and what you’ve been paid so far.', sizes: ['small', 'medium'] },
  { id: 'accountsList', group: 'Your accounts', title: 'Accounts', desc: 'Every account with its balance, last trading day and how much room it has before max loss.', sizes: ['medium', 'large'] },
  { id: 'growth', group: 'Your accounts', title: 'Balance & growth', desc: 'A real-money account: start, deposits, withdrawals and what trading made, kept apart.', sizes: ['medium', 'large'] },
  { id: 'toolCosts', group: 'Your accounts', title: 'Tool costs vs profit', desc: 'Courses, data and platforms against what your real-money accounts made.', sizes: ['small', 'medium'] },
  { id: 'account', group: 'Your accounts', title: 'Account status', desc: 'Balance, distance to the max loss, daily limit, consistency and payout progress for your accounts.', sizes: ['large'] },
  { id: 'performance', group: 'Summary', title: 'Performance summary', desc: 'The eight stat tiles with your balance curve and daily P&L, as on the classic Dashboard.', sizes: ['large'] },
  { id: 'summary', group: 'Summary', title: 'Summary and charts', desc: 'Net P&L, trades, today and this week with your balance curve (and, at half width, your accounts on top and your daily P&L).', sizes: ['medium', 'large'] },
  { id: 'kpi', group: 'Summary', title: 'Key numbers', desc: 'Net P&L with this month, fees and your average trade, plus trades, today and this week, in one card.', sizes: ['medium', 'large'] },
  { id: 'avgWinLoss', group: 'Summary', title: 'Average win vs average loss', desc: 'Your payoff ratio and the win rate it takes to break even.', sizes: ['small', 'medium'] },
  { id: 'mom', group: 'Summary', title: 'Month over month', desc: 'This month next to last month on the numbers that matter, better or worse.', sizes: ['medium', 'large'] },
  { id: 'scoreBar', group: 'Summary', title: 'Trader score bar', desc: 'Your trader score and its six parts in one slim bar.', sizes: ['large'] },
  { id: 'score', group: 'Summary', title: 'Trader score', desc: 'One score from 0 to 100 for your last 30 trading days, with its six parts and what would lift it.', sizes: ['medium', 'large'] },
  { id: 'monthly', group: 'Charts', title: 'Monthly P&L', desc: 'One bar per month for your last 12 months.', sizes: ['medium', 'large'] },
  { id: 'heatmap', group: 'Calendar', title: 'Calendar heatmap', desc: 'Your latest month as small green and red squares.', sizes: ['small', 'medium'] },
  { id: 'setups', group: 'Charts', title: 'P&L by setup', desc: 'Your setups and Playbook plays ranked by what they made.', sizes: ['medium', 'large'] },
  { id: 'byHour', group: 'Charts', title: 'P&L by time of day', desc: 'When you make and lose money, hour by hour (New York time).', sizes: ['medium', 'large'] },
  { id: 'byWeekday', group: 'Charts', title: 'P&L by weekday', desc: 'Your best and worst days of the week.', sizes: ['small', 'medium'] },
  { id: 'bySymbol', group: 'Charts', title: 'P&L by symbol', desc: 'Which instruments make and lose you money.', sizes: ['medium', 'large'] },
  { id: 'glance', group: 'Charts', title: 'At a glance', desc: 'Net P&L, win rate and trades by day, week or month, on one timeline.', sizes: ['medium', 'large'] },
  { id: 'equity', group: 'Charts', title: 'Equity curve & drawdown', desc: 'Your running P&L day by day, with the drops from your best point shaded red.', sizes: ['medium', 'large'] },
  { id: 'dailyBars', group: 'Charts', title: 'Daily P&L', desc: 'One bar per trading day for your last 30 trading days, green or red.', sizes: ['medium', 'large'] },
  { id: 'calendar', group: 'Calendar', title: 'P&L calendar', desc: 'Your month day by day, with notes and trades.', sizes: ['medium', 'large'] },
  { id: 'discipline', group: 'Discipline', title: 'Discipline score', desc: 'How often you followed your rules on the days you traded, rule by rule, and your revenge trades.', sizes: ['medium', 'large'] },
  { id: 'mistakes', group: 'Discipline', title: 'What your mistakes cost', desc: 'Each mistake tag with how often it happened and what it cost, worst first.', sizes: ['medium', 'large'] },
  { id: 'afterLoss', group: 'Discipline', title: 'Trading after a loss', desc: 'Your win rate on the trade right after a loss, next to your win rate overall.', sizes: ['small', 'medium'] },
  { id: 'rules', group: 'Discipline', title: 'Rules followed today', desc: 'Your Morning prep rules, ticked for today, and how many days in a row they held.', sizes: ['small', 'medium'] },
  { id: 'markFocus', group: 'Discipline', title: 'MARK’s coaching focus', desc: 'The one thing MARK has you working on, and how often the rule held this week.', sizes: ['medium', 'large'] },
  { id: 'positions', group: 'Trades', title: 'Open positions', desc: 'Swing trades synced from Interactive Brokers that are still open, with their P&L.', sizes: ['medium', 'large'] },
  { id: 'dayInsights', group: 'Insights', title: 'Day insights', desc: 'Your latest trading day’s mistakes, what they cost, how often they come back and the rule to fix them.', sizes: ['medium', 'large'] },
  { id: 'leak', group: 'Insights', title: 'Biggest leak', desc: 'What’s costing you the most right now, with a rule you can add.', sizes: ['medium', 'large'] },
  { id: 'needsReview', group: 'Trades', title: 'Needs review', desc: 'Trades still waiting for tags or notes, with a button to review them.', sizes: ['small', 'medium'] },
  { id: 'tradeTable', group: 'Trades', title: 'Trades table', desc: 'Your best, worst and untagged trades as one table.', sizes: ['medium', 'large'] },
  { id: 'insightRow', group: 'Insights', title: 'Top insight and reminders', desc: 'The most useful thing your trades show right now, plus trades to journal and payout progress.', sizes: ['large'] },
  { id: 'tradeLists', group: 'Trades', title: 'Best, worst and waiting trades', desc: 'Your five best and worst trades, and trades still waiting for tags or notes.', sizes: ['large'] },
  { id: 'recent', group: 'Trades', title: 'Recent trades', desc: 'Your latest trades, newest first. Click one to open it.', sizes: ['medium', 'large'] },
  { id: 'events', group: 'Market', title: "Today's market events", desc: 'News that can move the market today, and the next big one.', sizes: ['medium', 'large'] },
  { id: 'net', group: 'Numbers', title: 'Net P&L', desc: 'Everything your trades made or lost, after fees.', sizes: ['small', 'medium'] },
  { id: 'trades', group: 'Numbers', title: 'Trades', desc: 'How many trades, winners and losers.', sizes: ['small', 'medium'] },
  { id: 'winRate', group: 'Numbers', title: 'Win rate', desc: 'The share of trades that made money.', sizes: ['small', 'medium'] },
  { id: 'profitFactor', group: 'Numbers', title: 'Profit factor', desc: 'What winners made divided by what losers lost.', sizes: ['small', 'medium'] },
  { id: 'avgWin', group: 'Numbers', title: 'Average win', desc: 'A typical winning trade.', sizes: ['small', 'medium'] },
  { id: 'avgLoss', group: 'Numbers', title: 'Average loss', desc: 'A typical losing trade.', sizes: ['small', 'medium'] },
  { id: 'today', group: 'Numbers', title: 'Today', desc: "Today's net P&L, trade by trade.", sizes: ['small', 'medium'] },
  { id: 'week', group: 'Numbers', title: 'This week', desc: 'Net P&L since Sunday, day by day.', sizes: ['small', 'medium'] },
  { id: 'expectancy', group: 'Numbers', title: 'Trade expectancy', desc: 'What you make or lose on an average trade, after fees.', sizes: ['small', 'medium'] },
  { id: 'dayWin', group: 'Numbers', title: 'Day win rate', desc: 'The share of your trading days that ended green.', sizes: ['small', 'medium'] },
  { id: 'streak', group: 'Numbers', title: 'Streaks', desc: 'Winning or losing days and trades in a row, right now.', sizes: ['small', 'medium'] },
  { id: 'drawdown', group: 'Numbers', title: 'Max drawdown', desc: 'The biggest drop from a high point of your running P&L.', sizes: ['small', 'medium'] },
  { id: 'balance', group: 'Numbers', title: 'Account balance', desc: 'The balance of the account picked at the top, with its total P&L.', sizes: ['small', 'medium'] },
];
const BY_ID = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));

// The classic Dashboard, top to bottom.
export const DEFAULT_LAYOUT = [
  { id: 'account', size: 'large' }, { id: 'performance', size: 'large' }, { id: 'glance', size: 'large' },
  { id: 'insightRow', size: 'large' }, { id: 'calendar', size: 'large' }, { id: 'tradeLists', size: 'large' },
];

// The Score dashboard (a built-in tab): accounts, numbers and charts on the left, the trader score
// on the right, then the rest as on Classic.
export const SCORE_LAYOUT = [
  { id: 'summary', size: 'medium' }, { id: 'score', size: 'medium' },
  { id: 'glance', size: 'large' }, { id: 'insightRow', size: 'large' }, { id: 'calendar', size: 'large' }, { id: 'tradeLists', size: 'large' },
];

// A widget can keep its own period instead of the dashboard's date range.
export const PERIODS = [
  { id: '30d', label: 'Last 30 days' }, { id: 'month', label: 'This month' }, { id: 'year', label: 'This year' }, { id: 'all', label: 'All time' },
];
// Widgets with nothing to scope (they don't use trades).
const NO_SETTINGS = new Set(['events', 'rules', 'markFocus', 'positions']);
// (Discipline, accounts, growth and tool costs still have their Style choice in the settings.)
// Widgets that are useful before any trades, so they never show a Sample.
const NO_SAMPLE = new Set(['dayInsights', 'accountsList', 'growth', 'toolCosts', 'events', 'account', 'propProgress', 'dailyLoss', 'consistency', 'payouts', 'rules', 'markFocus', 'positions']);

function inPeriod(trades, period) {
  if (!period || period === 'all') return trades;
  const today = currentTradingDay();
  if (period === 'month') return trades.filter((x) => tradingDayKey(x.date).slice(0, 7) === today.slice(0, 7));
  if (period === 'year') return trades.filter((x) => tradingDayKey(x.date).slice(0, 4) === today.slice(0, 4));
  const from = new Date(`${today}T12:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 29);
  const fromKey = from.toISOString().slice(0, 10);
  return trades.filter((x) => tradingDayKey(x.date) >= fromKey);
}

// The context one widget sees: the dashboard's, or narrowed to its own account and period.
function scopedCtx(ctx, w) {
  if (!w || (!w.account && !w.period)) return ctx;
  const account = w.account ? ctx.accounts.find((a) => String(a._id) === w.account) : null;
  if (w.account && !account) return ctx;
  const base = account ? ctx.allTrades.filter((x) => String(x.accountId) === w.account) : ctx.accountTrades;
  const trades = inPeriod(base, w.period);
  return { ...ctx, trades, accountTrades: account ? trades : ctx.accountTrades, account: account || ctx.account, shownIds: account ? [account._id] : ctx.shownIds, narrowed: true };
}

// Starting points for a new dashboard. Every widget fits a row of 12 columns exactly.
export const TEMPLATES = [
  { id: 'prop', name: 'Account rules', desc: 'Stay inside your firm’s rules and get funded: progress to pass, daily loss left, consistency and payouts.', chips: ['Account progress', 'Daily loss left', 'Consistency rule', 'Key numbers'],
    layout: [{ id: 'propProgress', size: 'large' }, { id: 'kpi', size: 'medium' }, { id: 'dailyLoss', size: 'small' }, { id: 'consistency', size: 'small' },
      { id: 'equity', size: 'medium' }, { id: 'dailyBars', size: 'medium' }, { id: 'leak', size: 'medium' }, { id: 'payouts', size: 'small' }, { id: 'needsReview', size: 'small' }] },
  { id: 'daily', name: 'Daily review', desc: 'Where today stands, what needs tagging, and the month day by day.', chips: ['Today', 'Needs review', 'Biggest leak', 'P&L calendar'],
    layout: [{ id: 'today', size: 'small' }, { id: 'week', size: 'small' }, { id: 'needsReview', size: 'small' }, { id: 'dayWin', size: 'small' },
      { id: 'dailyBars', size: 'medium' }, { id: 'leak', size: 'medium' }, { id: 'calendar', size: 'large' }, { id: 'tradeTable', size: 'large' }] },
  { id: 'edge', name: 'Find my edge', desc: 'Your trader score, expectancy, streaks and drawdown, and how they change over time.', chips: ['Trader score bar', 'Trade expectancy', 'Max drawdown', 'At a glance'],
    layout: [{ id: 'scoreBar', size: 'large' }, { id: 'expectancy', size: 'small' }, { id: 'dayWin', size: 'small' }, { id: 'streak', size: 'small' }, { id: 'drawdown', size: 'small' },
      { id: 'glance', size: 'large' }, { id: 'equity', size: 'medium' }, { id: 'leak', size: 'medium' }] },
  { id: 'command', name: 'Command center', desc: 'Your key numbers, account rules and charts on one screen, each widget in the style you like.', chips: ['Net P&L', 'Win rate', 'Account progress', 'Daily loss left'],
    layout: [{ id: 'net', size: 'small' }, { id: 'winRate', size: 'small' }, { id: 'profitFactor', size: 'small' }, { id: 'streak', size: 'small' },
      { id: 'propProgress', size: 'large' },
      { id: 'dailyLoss', size: 'small' }, { id: 'consistency', size: 'small' }, { id: 'payouts', size: 'small' }, { id: 'needsReview', size: 'small' },
      { id: 'equity', size: 'medium' }, { id: 'byHour', size: 'medium' }] },
  { id: 'score', name: 'Trader score', desc: 'The Score tab as a starting point: your accounts and numbers on the left, your full trader score on the right.', chips: ['Summary and charts', 'Trader score', 'At a glance', 'P&L calendar'],
    layout: SCORE_LAYOUT },
];

// A template's layout drawn as small blocks.
function LayoutSketch({ layout }) {
  return (
    <div className="dw-sketch" aria-hidden="true">
      {layout.map((w) => {
        const group = BY_ID[w.id] ? BY_ID[w.id].group : '';
        return <i key={w.id} className={`k-${group === 'Account rules' ? 'prop' : group === 'Charts' || group === 'Calendar' ? 'chart' : 'num'} h-${w.id === 'calendar' || w.id === 'glance' ? 'tall' : w.size}`} style={{ gridColumn: `span ${SPAN[w.size]}` }} />;
      })}
    </div>
  );
}

// A new dashboard starts here: three templates, a blank one, or a copy of Classic.
function TemplatePicker({ recommended, onPick, onCancel }) {
  const { t } = useI18n();
  return (
    <section className="dw-picker">
      <div className="dw-picker-box">
        <h2>{t('Build a dashboard that fits how you trade')}</h2>
        <p className="dov-muted">{t('Start from a layout and change anything, or start from nothing and add only what you need.')}</p>
        <div className="dw-templates">
          {TEMPLATES.map((tpl) => (
            <button key={tpl.id} type="button" className={`dw-template${tpl.id === recommended ? ' recommended' : ''}`} onClick={() => onPick(tpl)}>
              <LayoutSketch layout={tpl.layout} />
              <strong>{t(tpl.name)}</strong>
              <span className="dov-muted">{t(tpl.desc)}</span>
              <span className="dw-template-chips">{tpl.chips.map((c) => <span key={c}>{t(c)}</span>)}</span>
              <small className={tpl.id === recommended ? 'cyan' : 'dov-muted'}>{t('{n} widgets', { n: tpl.layout.length })}{tpl.id === recommended ? ` · ${t('Recommended')}` : ''}</small>
            </button>
          ))}
        </div>
        <span className="dw-picker-or">{t('or')}</span>
        <div className="dw-picker-actions">
          <button type="button" className="small" onClick={() => onPick(null)}>+ {t('Start blank and add widgets')}</button>
          <button type="button" className="secondary small" onClick={() => onPick({ id: 'classic', name: 'Classic copy', layout: DEFAULT_LAYOUT })}>{t('Copy my Classic dashboard')}</button>
          <button type="button" className="secondary small" onClick={onCancel}>{t('Cancel')}</button>
        </div>
        <small className="dov-muted">{t('You can change everything later. Dashboards save on their own.')}</small>
      </div>
    </section>
  );
}

// A saved layout, cleaned: known widgets only, each once, in a size it fits.
export function cleanLayout(layout) {
  if (!Array.isArray(layout)) return DEFAULT_LAYOUT;
  const seen = new Set();
  return layout.filter((w) => w && (BY_ID[w.id] || isGap(w)) && !seen.has(w.id) && seen.add(w.id))
    .map((w) => (isGap(w) ? { id: w.id, size: 'small' } : {
      id: w.id, size: BY_ID[w.id].sizes.includes(w.size) ? w.size : BY_ID[w.id].sizes[BY_ID[w.id].sizes.length - 1],
      ...(typeof w.account === 'string' && w.account ? { account: w.account } : {}),
      ...(PERIODS.some((p) => p.id === w.period) ? { period: w.period } : {}),
      ...(STYLES[w.id] && STYLES[w.id].some(([sid]) => sid === w.style) ? { style: w.style } : {}),
      // Its place on the grid, with the width kept within the sizes the widget allows.
      ...(gridInt(w.x, 0, 11) && gridInt(w.y, 0, 5000) && gridInt(w.w, 1, 12) && gridInt(w.h, 1, 400) ? (() => {
        const sizes = BY_ID[w.id].sizes;
        const width = Math.min(SPAN[sizes[sizes.length - 1]], Math.max(SPAN[sizes[0]], w.w));
        return { x: Math.min(w.x, 12 - width), y: w.y, w: width, h: w.h, size: width <= 3 ? 'small' : width <= 6 ? 'medium' : 'large' };
      })() : {}),
      ...(w.hLocked ? { hLocked: true } : {}),
    }));
}

// --- The widgets ---------------------------------------------------------------------------

function RecentTrades({ trades, onEditTrade }) {
  const { t } = useI18n();
  const list = [...trades].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8);
  return (
    <section className="dash-card dw-card">
      <h3 className="dw-title">{t('Recent trades')}</h3>
      <ul className="trade-mini-list">
        {list.map((x) => (
          <li key={x._id}>
            <button type="button" className="trade-mini-row" onClick={() => onEditTrade(x)}>
              <span className="trade-mini-main"><strong>{x.symbol}</strong> <span className="trade-mini-side">{t(x.side || 'Long')} &times; {x.size || 1}</span>
                <small>{new Date(x.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}</small></span>
              <span className={`trade-mini-pnl ${tone(netPnl(x))}`}>{formatMoney(netPnl(x))}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// The number widgets beyond the eight classic tiles, each with its own small graphic.
function StreakTile({ trades }) {
  const { t } = useI18n();
  const now = currentStreaks(trades);
  const best = longestStreaks(trades);
  const cols = [['days', 'Days', '{n} day', '{n} days'], ['trades', 'Trades', '{n} trade', '{n} trades']];
  const unit = (n, one, many) => t(n === 1 ? one : many, { n });
  return (
    <div className="stat dw-fancy">
      <span className="stat-label">{t('Current streak')}<InfoTip label={t('Current streak')} text={t('The big number is your streak right now: green for wins in a row, red for losses. Beside it, your longest losing streak (red) and longest winning streak (green). A breakeven day or trade ends a streak.')} /></span>
      <div className="dw-streak">
        {cols.map(([key, label, one, many]) => {
          const c = now[key];
          return (
            <div key={key} className="dw-streak-col">
              <span className="dw-streak-head">{t(label)}</span>
              <div className="dw-streak-row">
                <span className={`dw-streak-circle ${c.winning === null ? '' : c.winning ? 'gain' : 'loss'}`} title={c.winning === null ? '' : t(c.winning ? 'Winning streak' : 'Losing streak')}>{c.count}</span>
                <span className="dw-chips">
                  <span className="dw-chip loss" title={t('Longest losing streak')}>{unit(best[key].loss, one, many)}</span>
                  <span className="dw-chip gain" title={t('Longest winning streak')}>{unit(best[key].win, one, many)}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayWinTile({ trades }) {
  const { t } = useI18n();
  const d = dayWinRate(trades);
  const last = dayNets(trades).slice(-10);
  return (
    <Tile label="Day win rate" info="The share of your trading days that ended green, after fees. The dots are your last 10 trading days, oldest first."
      value={d ? `${Math.round(d.rate)}%` : '—'}
      note={d ? <span className="dw-chips inline"><span className="dw-chip gain">{t('{n} green', { n: d.green })}</span><span className="dw-chip loss">{t('{n} red', { n: d.days - d.green })}</span></span> : ''}
      visual={d ? <Ring share={d.rate / 100} /> : null}
      bar={last.length ? <span className="dw-dots" aria-hidden="true">{last.map((x) => <span key={x.key} className={x.net > 0 ? 'gain' : x.net < 0 ? 'loss' : ''} />)}</span> : null} />
  );
}

function ExpectancyTile({ trades }) {
  const { t } = useI18n();
  const e = expectancy(trades);
  const p = expectancyParts(trades);
  const total = p ? p.fromWins - p.fromLosses : 0;
  return (
    <Tile label="Trade expectancy" info="What you make or lose on an average trade, after fees. The bar shows what your wins add to each trade (green) and what your losses take away (red)."
      value={e === null ? '—' : formatMoney(e)} className={tone(e)}
      note={p ? t('{wins} from wins · {losses} from losses per trade', { wins: formatMoney(p.fromWins), losses: formatMoney(p.fromLosses) }) : ''}
      bar={p && total ? <span className="dw-split" aria-hidden="true"><span className="gain" style={{ flexGrow: p.fromWins }} /><span className="loss" style={{ flexGrow: -p.fromLosses }} /></span> : null} />
  );
}

function Underwater({ points }) {
  if (points.length < 2) return null;
  const w = 200;
  const h = 34;
  const low = Math.min(...points) || -1;
  const xy = points.map((v, i) => `${((i / (points.length - 1)) * w).toFixed(1)},${((v / low) * (h - 2) + 1).toFixed(1)}`);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="dw-underwater" aria-hidden="true">
      <path d={`M0,1 L${xy.join(' L')} L${w},1 Z`} className="dw-under-area" />
      <path d={`M${xy.join(' L')}`} className="dw-under-line" />
      <line x1="0" x2={w} y1="1" y2="1" className="dw-under-zero" />
    </svg>
  );
}

function DrawdownTile({ trades }) {
  const { t } = useI18n();
  const d = maxDrawdown(trades);
  const series = drawdownSeries(trades);
  const day = d.day ? new Date(`${d.day}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return (
    <Tile label="Max drawdown" info="The biggest drop from a high point of your running P&L, trade by trade, after fees. The red shape shows how far below your high you were over time."
      value={d.amount ? formatMoney(d.amount) : '$0.00'} className={d.amount ? 'loss' : ''}
      note={d.day ? <>{t('Bottomed on {day}', { day })}{' · '}{series.now < 0 ? t('{amount} below your high now', { amount: usd.format(-series.now) }) : t('At your high now')}</> : ''}
      bar={<Underwater points={series.points} />} />
  );
}

function BalanceTile({ account, trades }) {
  const { t } = useI18n();
  const st = account ? accountStatus(account, trades) : null;
  if (!st) return <Tile label="Account balance" value="—" note={t('Pick an account at the top to see its balance.')} />;
  let running = account.size || 0;
  const line = [running, ...[...trades].sort((a, b) => new Date(a.date) - new Date(b.date)).map((x) => (running += netPnl(x)))];
  return (
    <Tile label="Account balance" info="The account's starting balance plus everything its trades made or lost, after fees, and any deposits or withdrawals."
      value={usd.format(st.balance)} note={<span className={`dw-chip ${tone(st.change)}`}>{t('{change} since the start', { change: formatMoney(st.change) })}</span>}
      visual={line.length > 2 ? <Sparkline values={line.map((v) => v - (account.size || 0))} /> : null} />
  );
}

const EXTRA_TILES = { streak: StreakTile, dayWin: DayWinTile, expectancy: ExpectancyTile, drawdown: DrawdownTile, balance: BalanceTile };

// Widgets with styles draw themselves; the rest are below.
function WidgetBody({ id, ctx: dashCtx, widgetSize, widget }) {
  const ctx = scopedCtx(dashCtx, widget);
  if (STYLES[id]) return <StyledWidget id={id} style={styleOf(widget || { id })} ctx={ctx} />;
  return <PlainWidget id={id} ctx={ctx} widgetSize={widgetSize} />;
}

function PlainWidget({ id, ctx, widgetSize }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { trades, accountTrades, allTrades, accounts, account, shownIds, size, library, notes, setNotes, narrowed, scopeLine, onEditTrade, onAddTrade, onManageAccounts, onAccountChange, onSelectAccount, onSessionExpired, tiles, TradeLists } = ctx;
  const accountBlock = () => (account ? <AccountStatusCard account={account} trades={accountTrades} onManage={() => onManageAccounts(account._id)} onAccountChange={onAccountChange} onSessionExpired={onSessionExpired} />
    : accounts.some((a) => !a.archived) ? <AccountOverview accounts={shownIds ? accounts.filter((a) => shownIds.includes(a._id)) : accounts} trades={allTrades} onSelect={onSelectAccount} /> : null);
  switch (id) {
    case 'account': return accountBlock();
    case 'performance': return <PerformancePanel trades={trades} account={account} size={size} />;
    case 'rules': return <RulesToday notes={notes} />;
    case 'dayInsights': return <LatestDayInsights trades={trades} accounts={accounts} notes={notes} setup={ctx.insights} />;
    case 'markFocus': return <MarkFocus />;
    case 'positions': return <OpenPositionsWidget accounts={accounts} />;
    case 'byWeekday': return <PnlByWeekday trades={trades} />;
    case 'bySymbol': return <PnlBySymbol trades={trades} />;
    case 'scoreBar': return <div className="dov"><ScoreBar trades={trades} /></div>;
    case 'kpi':
      return <div className="performance dw-summary half dw-kpi"><div className="performance-tiles">{tiles.netWide}{tiles.trades}{tiles.today}{tiles.week}</div></div>;
    case 'leak': return <div className="dov"><LeakCard trades={trades} library={library} /></div>;
    case 'tradeTable': return <div className="dov"><TradeTable trades={trades} onEditTrade={onEditTrade} /></div>;
    case 'score': return <TraderScoreSection trades={trades} data={ctx.scoreData} headMoved={Boolean(ctx.scoreData && ctx.scoreData.now)} />;
    case 'summary':
      return (
        <section className={`performance dw-summary${widgetSize === 'medium' ? ' half' : ''}`}>
          {widgetSize === 'medium' && accountBlock()}
          <div className="performance-tiles">{widgetSize === 'medium' ? tiles.netWide : tiles.net}{tiles.trades}{tiles.today}{tiles.week}</div>
          <div className="dash-card dw-card"><EquityCurve trades={trades} startingBalance={account && account.size ? account.size : null} cashFlows={account ? account.cashFlows : []} /></div>
          {widgetSize === 'medium' && <div className="dash-card dw-card"><DailyBars trades={trades} /></div>}
        </section>
      );
    case 'glance': return <PeriodPanels trades={trades} title={t('At a glance')} note={t('Net P&L, win rate and trades by day, week or month. Hover for the numbers.')} />;
    case 'dailyBars': return <div className="dash-card dw-card"><DailyBars trades={trades} /></div>;
    case 'calendar': return <Calendar trades={trades} account={account} size={size} insights={ctx.insights} library={library} notes={notes} setNotes={setNotes} onEditTrade={onEditTrade} onAddTrade={onAddTrade} onSessionExpired={onSessionExpired} />;
    case 'insightRow':
      return (
        <div className="insight-row">
          <InsightCard trades={trades} library={library} recent={!narrowed} scopeLine={narrowed ? scopeLine : ''} onOpen={() => navigate('/app/insights')} />
          <Nudges trades={accountTrades} account={account} onEditTrade={onEditTrade} showUnjournaled={false} />
        </div>
      );
    case 'tradeLists': return <TradeLists trades={trades} onEditTrade={onEditTrade} />;
    case 'recent': return <RecentTrades trades={trades} onEditTrade={onEditTrade} />;
    case 'events': return <TodayEvents />;
    default: {
      const Extra = EXTRA_TILES[id];
      const tile = tiles[id] || (Extra ? <Extra trades={trades} account={account} /> : null);
      return tile ? <div className="performance-tiles dw-tile">{tile}</div> : null;
    }
  }
}

// --- The widget library ----------------------------------------------------------------------

// Each widget drawn small with the member's own numbers, so they see what they're adding.
const PREVIEW_SCALE = { small: 0.7, medium: 0.42, large: 0.36 };
function LivePreview({ widget, render }) {
  const size = widget.sizes.includes('small') ? 'small' : widget.sizes.includes('medium') ? 'medium' : 'large';
  const scale = PREVIEW_SCALE[size];
  return (
    <div className={`dw-live dw-live-${size}`} aria-hidden="true">
      <div className="dw-live-inner" style={{ width: `${100 / scale}%`, transform: `scale(${scale})` }}>{render(widget.id)}</div>
    </div>
  );
}

// Before any trades: grey shapes marked Sample, never made-up numbers.
function SamplePreview({ widget }) {
  const { t } = useI18n();
  const chart = widget.group === 'Charts' || widget.group === 'Calendar';
  return (
    <div className="dw-live dw-sample" aria-hidden="true">
      <span className="dw-sample-tag">{t('Sample')}</span>
      {chart ? <span className="dw-sample-bars">{[40, 65, 30, 80, 55, 70, 45, 90, 60].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</span>
        : <span className="dw-sample-lines"><i style={{ width: '40%' }} /><i className="big" style={{ width: '62%' }} /><i style={{ width: '80%' }} /></span>}
    </div>
  );
}

// The library: a drawer on the right that stays open while widgets are added, with search,
// category chips and an "Added" mark on the widgets already on the dashboard.
function Library({ layout, onAdd, onClose, render, sample = false }) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const on = new Set(layout.map((w) => w.id));
  const query = q.trim().toLowerCase();
  const allGroups = [...new Set(WIDGETS.map((w) => w.group))];
  const list = WIDGETS.filter((w) => (!group || w.group === group) && (!query || `${t(w.title)} ${t(w.desc)} ${t(w.group)}`.toLowerCase().includes(query)));
  const groups = [...new Set(list.map((w) => w.group))];
  return createPortal(
    <aside className="dw-drawer" role="dialog" aria-label={t('Widget library')}>
      <div className="dw-drawer-top">
        <div><h2>{t('Add widgets')}</h2><p className="field-hint dw-lib-sub">{t('Click to add. Each preview uses your own trades, and every widget follows the dashboard’s accounts and dates.')}</p></div>
        <button type="button" className="icon-button" aria-label={t('Close')} onClick={onClose}>&times;</button>
      </div>
      <input type="search" className="dw-search" placeholder={t('Search widgets')} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      <div className="dw-chips" role="group" aria-label={t('Categories')}>
        <button type="button" className={!group ? 'on' : ''} aria-pressed={!group} onClick={() => setGroup('')}>{t('All')}</button>
        {allGroups.map((g) => <button key={g} type="button" className={group === g ? 'on' : ''} aria-pressed={group === g} onClick={() => setGroup(g)}>{t(g)}</button>)}
      </div>
      <div className="dw-drawer-list">
        {!list.length && <p className="empty">{t('No widget matches that search.')}</p>}
        {groups.map((g) => (
          <section key={g} className="dw-lib-group">
            <h3>{t(g)}</h3>
            <ul>
              {list.filter((w) => w.group === g).map((w) => (
                <li key={w.id} className={`dw-lib-card${on.has(w.id) ? ' on' : ''}`}>
                  {sample ? <SamplePreview widget={w} /> : <LivePreview widget={w} render={render} />}
                  <div className="dw-lib-text">
                    <div><strong>{t(w.title)}</strong><p>{t(w.desc)}</p>{STYLES[w.id] && <small className="dw-lib-styles">{t('{n} styles · pick one in the widget’s settings', { n: STYLES[w.id].length })}</small>}</div>
                    {on.has(w.id) ? <span className="dw-on">✓ {t('Added')}</span>
                      : <button type="button" className="small" onClick={() => onAdd(w.id)}>+ {t('Add')}</button>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <button type="button" className="small dw-drawer-done" onClick={onClose}>{t('Done adding')}</button>
    </aside>,
    document.body,
  );
}

// True on phone-width screens, where editing becomes a simple reorder list.
function useNarrow() {
  const query = '(max-width: 700px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

// One widget's own settings: which account it shows and which period, instead of the dashboard's.
function WidgetSettings({ widget, accounts, onChange, onClose, preview }) {
  const { t } = useI18n();
  const styles = STYLES[widget.id];
  const set = (key, value) => {
    const next = { ...widget };
    if (value) next[key] = value; else delete next[key];
    onChange(next);
  };
  return (
    <div className={`dw-settings${styles ? ' with-styles' : ''}`} role="dialog" aria-label={t('Widget settings')}>
      {styles && (
        <div className="dw-styles">
          <span className="dw-styles-label">{t('Style')}</span>
          <div className="dw-style-list">
            {styles.map(([sid, label]) => (
              <button key={sid} type="button" className={`dw-style${styleOf(widget) === sid ? ' on' : ''}`} aria-pressed={styleOf(widget) === sid} onClick={() => set('style', sid === styles[0][0] ? '' : sid)}>
                <span className="dw-style-preview" aria-hidden="true"><span>{preview(sid)}</span></span>
                <b>{t(label)}</b>
              </button>
            ))}
          </div>
        </div>
      )}
      <label>{t('Account')}
        <select value={widget.account || ''} onChange={(e) => set('account', e.target.value)}>
          <option value="">{t('Follow the dashboard')}</option>
          {accounts.filter((a) => !a.archived).map((a) => <option key={a._id} value={String(a._id)}>{a.name}</option>)}
        </select>
      </label>
      <label>{t('Period')}
        <select value={widget.period || ''} onChange={(e) => set('period', e.target.value)}>
          <option value="">{t('Follow the dashboard’s dates')}</option>
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{t(p.label)}</option>)}
        </select>
      </label>
      <small className="dov-muted">{t('Everything else follows the filters at the top of the page.')}</small>
      <button type="button" className="small" onClick={onClose}>{t('Done')}</button>
    </div>
  );
}

// Phones: editing is a list to reorder and remove, not a grid to drag on.
function PhoneEditList({ layout, onMove, onRemove, onAdd }) {
  const { t } = useI18n();
  // Empty slots don't matter on a phone: widgets stack, so only their order shows.
  const real = readingOrder(layout.map((w, i) => ({ ...w, i: undefined, idx: i }))).map((w) => ({ w, i: w.idx }));
  return (
    <ul className="dw-phone-list">
      {real.map(({ w, i }, k) => (
        <li key={w.id}>
          <span className="dw-grip" aria-hidden="true">⠿</span>
          <span className="dw-phone-name"><b>{t(BY_ID[w.id].title)}</b><small>{t(SIZE_NAME[w.size])}</small></span>
          <button type="button" className="icon-button" aria-label={t('Move up')} disabled={k === 0} onClick={() => onMove(i, real[k - 1].i)}>↑</button>
          <button type="button" className="icon-button" aria-label={t('Move down')} disabled={k === real.length - 1} onClick={() => onMove(i, real[k + 1].i)}>↓</button>
          <button type="button" className="icon-button dw-remove" aria-label={t('Remove {name}', { name: t(BY_ID[w.id].title) })} onClick={() => onRemove(i)}>&times;</button>
        </li>
      ))}
      <li className="dw-phone-add"><button type="button" className="small" onClick={onAdd}>+ {t('Add widget')}</button></li>
    </ul>
  );
}

// The dashboard menu: every dashboard by name in one dropdown, instead of a long row of tabs.
function DashboardMenu({ items, activeId, onPick, onNew }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key); };
  }, [open]);
  const current = items.find((d) => d.id === activeId) || items[0];
  return (
    <div className="dw-menu" ref={ref}>
      <button type="button" className="dw-menu-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="dw-menu-label">{t('Dashboard')}</span> <b>{current.name}</b> <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="dw-menu-list" role="menu">
          {items.map((d, i) => (
            <Fragment key={d.id}>
              {i > 0 && items[i - 1].builtIn && !d.builtIn && <span className="dw-menu-sep">{t('Your dashboards')}</span>}
              <button type="button" role="menuitemradio" aria-checked={d.id === activeId} className={d.id === activeId ? 'on' : ''} onClick={() => { setOpen(false); onPick(d.id); }}>
                <span>{d.name}</span>{d.id === activeId && <span aria-hidden="true">✓</span>}
              </button>
            </Fragment>
          ))}
          {onNew && <button type="button" className="dw-menu-new" role="menuitem" onClick={() => { setOpen(false); onNew(); }}>+ {t('New dashboard')}</button>}
        </div>
      )}
    </div>
  );
}

// --- Dashboards, the grid and Customize mode ----------------------------------------------------

const MAX_DASHBOARDS = 10;
const SOFT_LIMIT = 12;
const BUILTIN_KEY = 'mm-dashboard-view';
const newId = () => `d${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

export default function DashboardWidgets({ user, onUserChange, ctx }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  // Custom dashboards: every member (not the demo) once Admin → Features opens them; admins always,
  // shown in gold until then.
  const dashboardsOpen = featureOpen(user, 'dashboards');
  const canCustomize = Boolean(user && !user.isDemo && (user.isAdmin || dashboardsOpen));
  const preview = !dashboardsOpen;
  const dashboards = useMemo(() => (canCustomize && user.dashboards) || [], [canCustomize, user]);
  // Built-in layouts everyone has (Overview first by default); admins' choice is saved to the
  // account, everyone else's on this device.
  const [localPick, setLocalPick] = useState(() => {
    try {
      return localStorage.getItem(BUILTIN_KEY) || 'overview';
    } catch {
      return 'overview';
    }
  });
  const builtIns = [{ id: 'classic', name: t('Classic') }, { id: 'overview', name: t('Overview') }, { id: 'board3', name: t('Snapshot') }, { id: 'board4', name: lang === 'es' ? 'Puntuación' : 'Score' }]; // not t('Score'): that's "Calificación" (trade grades)
  const isBuiltInView = (id) => id === 'overview' || id === 'board3' || id === 'board4';
  const activeId = canCustomize
    ? (dashboards.some((d) => d.id === user.activeDashboard) || isBuiltInView(user.activeDashboard) ? user.activeDashboard
      // A member who hasn't switched since dashboards opened keeps the tab they picked on this device.
      : user.activeDashboard ? 'classic' : (['classic', 'board3', 'board4'].includes(localPick) ? localPick : 'overview'))
    : (['classic', 'board3', 'board4'].includes(localPick) ? localPick : 'overview');
  const active = dashboards.find((d) => d.id === activeId) || null;
  const saved = useMemo(() => cleanLayout(active ? active.layout : null), [active]);
  // While customizing: { id, name, layout, start (the layout when editing began, for Reset), dirty }.
  // Changes save on their own a moment after the last one.
  const [draft, setDraft] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [library, setLibrary] = useState(false);
  const [settingsFor, setSettingsFor] = useState(null);
  // The empty slot that was clicked, so the next widget added goes there.
  const [placeSlot, setPlaceSlot] = useState(null);
  const narrow = useNarrow();
  const [status, setStatus] = useState('saved');
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const layout = draft ? draft.layout : activeId === 'board4' ? SCORE_LAYOUT : saved;
  const tiles = performanceTiles(ctx.trades, ctx.size, t);
  // Worked out once for the Score dashboard: its heading sits in the tab row.
  const scoreData = useMemo(() => (activeId === 'board4' ? traderScore(ctx.trades) : undefined), [activeId, ctx.trades]);
  const render = (id, widgetSize, widget) => {
    const own = widget && (widget.account || widget.period);
    return <WidgetBody id={id} widgetSize={widgetSize} widget={widget} ctx={{ ...ctx, tiles: own ? performanceTiles(scopedCtx(ctx, widget).trades, ctx.size, t) : tiles, scoreData }} />;
  };

  // The latest user and dashboards, so saving never works from an old copy.
  const latest = useRef({ user, onUserChange, dashboards });
  latest.current = { user, onUserChange, dashboards };
  // Each call changes one dashboard (or which one shows) on the server, so another tab's older copy
  // of the list can't overwrite it.
  const apply = useCallback((r) => latest.current.onUserChange({ ...latest.current.user, dashboards: r.dashboards, activeDashboard: r.activeDashboard }), []);
  const saveOne = useCallback(async (d) => {
    apply(await api(`/dashboards/${d.id}`, { method: 'PUT', body: { name: d.name.trim() || d.fallbackName, layout: d.layout, active: true } }));
  }, [apply]);
  const persist = saveOne;
  // Autosave: a moment after the last change. Once saved, the draft is clean until the next change.
  useEffect(() => {
    if (!draft || !draft.dirty) return undefined;
    setStatus('saving');
    const timer = setTimeout(() => {
      persist(draft).then(() => {
        setStatus('saved');
        setError('');
        setDraft((cur) => (cur && cur.id === draft.id && cur.layout === draft.layout && cur.name === draft.name ? { ...cur, dirty: false } : cur));
      }).catch((err) => { setStatus('error'); setError(err.message); });
    }, 700);
    return () => clearTimeout(timer);
  }, [draft, persist]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const pick = (id) => {
    if (draft || id === activeId) return;
    if (!canCustomize) {
      setLocalPick(id);
      try {
        localStorage.setItem(BUILTIN_KEY, id);
      } catch {
        // Not remembered in private mode.
      }
      return;
    }
    api('/dashboard-active', { method: 'PUT', body: { active: id } }).then(apply).catch((err) => setError(err.message));
  };

  const change = (fn) => setDraft((d) => ({ ...d, layout: fn(d.layout), dirty: true }));
  // Edit mode works on the grid: positions and sizes; the grid keeps widgets from overlapping.
  const commit = (next) => { setSettingsFor(null); change(() => next); };
  const resize = (i, size) => change((list) => list.map((w, j) => (j === i ? { ...w, size, w: WIDTH[size], x: Math.min(w.x, 12 - WIDTH[size]) } : w)));
  const configure = (i, next) => change((list) => list.map((w, j) => (j === i ? next : w)));
  const scopeText = (w) => {
    const acct = w.account && ctx.accounts.find((a) => String(a._id) === w.account);
    const period = w.period && PERIODS.find((p) => p.id === w.period);
    return [acct && acct.name, period && t(period.label)].filter(Boolean).join(' · ');
  };
  const remove = (i) => {
    setSettingsFor(null);
    const before = draft.layout;
    change((list) => list.filter((_, j) => j !== i));
    setToast({ text: t('{name} removed', { name: t(BY_ID[before[i].id].title) }), undo: before });
  };
  // A widget goes in the slot that was clicked (or the first empty one), at the smallest width it
  // allows; dragging its corner or S, M and L make it bigger.
  const add = (id) => {
    const target = placeSlot;
    change((list) => {
      const spot = target || freeSlots(compact(list))[0] || { x: 0, y: 0 };
      const w = WIDTH[BY_ID[id].sizes[0]];
      return [...list, { id, size: sizeOfW(w), x: Math.min(spot.x, 12 - w), y: spot.y, w, h: guessH(id, w) }];
    });
    if (target) { setPlaceSlot(null); setLibrary(false); }
  };
  const openSlot = (slot) => { setPlaceSlot(slot); setLibrary(true); };
  // Phones: swap two widgets' places.
  const swap = (i, j) => change((list) => {
    const next = [...list];
    const [a, b] = [next[i], next[j]];
    next[i] = { ...a, x: b.x, y: b.y };
    next[j] = { ...b, x: a.x, y: a.y };
    return next;
  });

  const nextName = () => t('Dashboard {n}', { n: dashboards.length + 1 });
  const builtInName = (builtIns.find((b) => b.id === activeId) || builtIns[0]).name;
  // Customizing a built-in layout (Classic, Score) makes the member's own copy on the first change.
  const startCustomize = () => {
    setStatus('saved');
    const fallbackName = t('My dashboard');
    const grid = toGrid(active ? saved : layout);
    setDraft(active ? { id: active.id, name: active.name, fallbackName, layout: grid, start: grid, dirty: false }
      : { id: newId(), name: t('{name} copy', { name: builtInName }), fallbackName, layout: grid, start: grid, dirty: false });
  };
  const startNew = () => setChoosing(true);
  // A template (or a blank dashboard, or a copy of Classic) becomes a new dashboard, open for editing.
  const startFrom = (tpl) => {
    setChoosing(false);
    setStatus('saved');
    const name = tpl ? t(tpl.name) : nextName();
    const list = tpl ? toGrid(tpl.layout) : [];
    setDraft({ id: newId(), name, fallbackName: name, layout: list, start: list, dirty: true });
  };
  const recommended = ctx.accounts.some((a) => !a.archived && isPropAccount(a)) ? 'prop' : 'daily';
  const done = async () => {
    const d = draft;
    setLibrary(false);
    setToast(null);
    if (d.dirty) {
      try { await persist(d); } catch (err) { setError(err.message); return; }
    }
    setDraft(null);
  };
  const duplicate = async () => {
    const entry = { id: newId(), name: t('{name} copy', { name: draft.name.trim() || draft.fallbackName }), layout: draft.layout };
    try {
      await saveOne(entry);
      setDraft({ ...entry, fallbackName: draft.fallbackName, start: entry.layout, dirty: false });
    } catch (err) { setError(err.message); }
  };
  const del = async () => {
    if (!window.confirm(t('Delete the dashboard “{name}”? Your trades aren’t affected.', { name: draft.name }))) return;
    try {
      apply(await api(`/dashboards/${draft.id}`, { method: 'DELETE' }));
      setDraft(null);
      setLibrary(false);
    } catch (err) { setError(err.message); }
  };
  const stored = draft && dashboards.some((d) => d.id === draft.id);
  const realCount = draft ? draft.layout.length : 0;
  const menuItems = [...builtIns.map((b) => ({ ...b, builtIn: true })), ...dashboards];

  return (
    <>
      {choosing && <TemplatePicker recommended={recommended} onPick={startFrom} onCancel={() => setChoosing(false)} />}
      {!draft && !choosing && (canCustomize ? (
        <div className="dw-tabs dw-tabs-menu">
          <DashboardMenu items={menuItems} activeId={activeId} onPick={pick} onNew={dashboards.length < MAX_DASHBOARDS ? startNew : null} />
          {activeId === 'board4' && scoreData && scoreData.now && <span className="dw-tabs-score"><ScoreHeading data={scoreData} /></span>}
          <span className="dw-tabs-end">
            {preview && <span className="admin-preview-badge">Admin only</span>}
            {!(activeId === 'overview' || activeId === 'board3') && <button type="button" className={`secondary small${preview ? ' admin-preview' : ''}`} onClick={startCustomize}>{t('Customize')}</button>}
          </span>
        </div>
      ) : (
        <div className="dw-tabs" role="tablist" aria-label={t('Your dashboards')}>
          {builtIns.map((d) => (
            <button key={d.id} type="button" role="tab" aria-selected={d.id === activeId} className={`dw-tab${d.id === activeId ? ' active' : ''}`} onClick={() => pick(d.id)}>{d.name}</button>
          ))}
          {activeId === 'board4' && scoreData && scoreData.now && <span className="dw-tabs-score"><ScoreHeading data={scoreData} /></span>}
        </div>
      ))}
      {draft && (
        <div className="dw-bar">
          <span className="dw-editing">{t('Editing')}</span>
          <input className="dw-name-input" aria-label={t('Dashboard name')} value={draft.name} maxLength={40} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value, dirty: true }))} />
          <span className="dw-bar-status">
            {t(realCount === 1 ? '{n} widget' : '{n} widgets', { n: realCount })} &middot;{' '}
            {status === 'saving' || (draft.dirty && status !== 'error') ? t('Saving…') : status === 'error' ? <span className="loss">{t('Not saved')}</span> : stored ? <span className="gain">✓ {t('Saved')}</span> : t('Your copy is made on the first change')}
          </span>
          <span className="dw-bar-actions">
            <button type="button" className="small" onClick={() => { setPlaceSlot(null); setLibrary(true); }}>+ {t('Add widget')}</button>
            <button type="button" className="secondary small" disabled={draft.layout === draft.start} onClick={() => change(() => draft.start)}>{t('Reset')}</button>
            {stored && <button type="button" className="secondary small" disabled={dashboards.length >= MAX_DASHBOARDS} onClick={duplicate}>{t('Duplicate')}</button>}
            {stored && <button type="button" className="secondary small dw-danger" onClick={del}>{t('Delete')}</button>}
            <button type="button" className="small dw-done" onClick={done}>{t('Done')}</button>
          </span>
          <span className="dw-bar-hint">
            {narrow ? t('Use the arrows to reorder your widgets. On a phone they stack in this order. Changes save on their own.') : t('Click an empty slot to add a widget there. Drag a widget by its name to move it, and pull its right edge (or pick S, M, L) to change its width. Changes save on their own.')}
            {realCount > SOFT_LIMIT && <> <span className="gold">{t('Dashboards work best with {n} widgets or fewer.', { n: SOFT_LIMIT })}</span></>}
          </span>
          {error && <p className="error">{error}</p>}
        </div>
      )}
      {!draft && error && <p className="error">{error}</p>}
      {choosing ? null : draft && narrow ? <PhoneEditList layout={draft.layout} onMove={swap} onRemove={remove} onAdd={() => { setPlaceSlot(null); setLibrary(true); }} /> : activeId === 'overview' && !draft ? <DashboardOverview ctx={ctx} /> : activeId === 'board3' && !draft ? <DashboardThree ctx={ctx} /> : (
      <>
      {!ctx.allTrades.length && layout.length > 0 && (
        <div className="dw-no-trades">
          <span><b>{t('Your widgets fill in once you have trades.')}</b> {t('Widgets marked Sample show what they’ll look like.')}</span>
          <button type="button" className="small" onClick={() => navigate('/app', { state: { scrollTo: 'import' } })}>{t('Import trades')}</button>
        </div>
      )}
      {draft || active ? (
        <DashGrid layout={draft ? draft.layout : toGrid(layout)} editing={Boolean(draft)} narrow={narrow} onCommit={commit} onSlot={openSlot}
          limits={(id) => [WIDTH[BY_ID[id].sizes[0]], WIDTH[BY_ID[id].sizes[BY_ID[id].sizes.length - 1]]]}
          renderItem={(w) => {
            const meta = BY_ID[w.id];
            const i = draft ? draft.layout.findIndex((x) => x.id === w.id) : -1;
            return (
              <div className={`dw-item dw-${w.size}${settingsFor === i && i >= 0 ? ' dw-open' : ''}${(w.x || 0) + (w.w || 12) / 2 < 6 ? ' pop-left' : ''}`}>
                {draft && (
                  <div className="dw-tools">
                    <span className="dw-name" title={t('Drag to move')}><span className="dw-grip" aria-hidden="true">⠿</span> {t(meta.title)}</span>
                    <span className="dw-tool-buttons">
                      {meta.sizes.length > 1 && meta.sizes.map((sz) => (
                        <button key={sz} type="button" className={`dw-size${w.size === sz ? ' active' : ''}`} aria-pressed={w.size === sz} title={t(SIZE_HINT[sz])} onClick={() => resize(i, sz)}>{SIZE_TEXT[sz]}</button>
                      ))}
                      {!NO_SETTINGS.has(w.id) && <button type="button" className={`icon-button dw-gear${w.account || w.period ? ' on' : ''}`} aria-label={t('Settings for {name}', { name: t(meta.title) })} aria-expanded={settingsFor === i} onClick={() => setSettingsFor(settingsFor === i ? null : i)}>⚙</button>}
                      <button type="button" className="icon-button dw-remove" aria-label={t('Remove {name}', { name: t(meta.title) })} onClick={() => remove(i)}>&times;</button>
                    </span>
                  </div>
                )}
                {!draft && (w.account || w.period) && <span className="dw-scope">{scopeText(w)}</span>}
                {draft && settingsFor === i && <WidgetSettings widget={w} accounts={ctx.accounts} onChange={(next) => configure(i, next)} onClose={() => setSettingsFor(null)} preview={(sid) => render(w.id, w.size, { ...w, style: sid })} />}
                <div className="dw-body">{!ctx.allTrades.length && !NO_SAMPLE.has(w.id)
                  ? <section className="dash-card dw-card dw-sample-card"><h3 className="dw-title">{t(meta.title)}</h3><SamplePreview widget={meta} /></section>
                  : render(w.id, w.size, w)}</div>
              </div>
            );
          }} />
      ) : null}
      {!draft && !active && (
      <div className="dw-grid">
        {layout.map((w) => (
          <div key={w.id} className={`dw-item dw-${w.size}`} style={{ '--span': SPAN[w.size] }}>
            {(w.account || w.period) && <span className="dw-scope">{scopeText(w)}</span>}
            <div className="dw-body">{render(w.id, w.size, w)}</div>
          </div>
        ))}
      </div>
      )}
      <div className="dw-grid">
        {!draft && !layout.length && (
          <div className="dw-item dw-empty" style={{ '--span': 12 }}>
            <b>{t('This dashboard has no widgets yet.')}</b>
            {canCustomize && <button type="button" className="small" onClick={() => { startCustomize(); setLibrary(true); }}>+ {t('Add widgets')}</button>}
          </div>
        )}
      </div>
      </>
      )}
      {library && draft && <Library layout={layout} onAdd={add} onClose={() => { setLibrary(false); setPlaceSlot(null); }} render={render} sample={!ctx.allTrades.length} />}
      {toast && draft && createPortal(
        <div className="dw-toast" role="status">
          <span>{toast.text}</span>
          <button type="button" className="small" onClick={() => { change(() => toast.undo); setToast(null); }}>{t('Undo')}</button>
          <button type="button" className="icon-button" aria-label={t('Close')} onClick={() => setToast(null)}>&times;</button>
        </div>,
        document.body,
      )}
    </>
  );
}
